import React, { useState, useEffect } from 'react';
import { Container, LinearProgress, Typography, Box } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { superAdminService } from '../services/superAdminService';
import type { DashboardStats as StatsType, SystemAlert as AlertType, RecentActivity as ActivityType } from '../services/superAdminService';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import DashboardStats from '../components/dashboard/DashboardStats';
import RecentActivity from '../components/dashboard/RecentActivity';
import SystemAlerts from '../components/dashboard/SystemAlerts';
import UserDialog from '../components/dashboard/UserDialog';
import SystemDialog from '../components/dashboard/SystemDialog';

const SuperAdminDashboard: React.FC = () => {
  const { user, hasAnyRole } = useAuth();

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<StatsType>({
    totalUsers: 0,
    totalJobs: 0,
    totalApplications: 0,
    totalCourses: 0,
    totalTests: 0,
    totalInterviews: 0,
    totalCertificates: 0,
    totalCourseRegistrations: 0,
    activeUsers: 0,
    pendingApplications: 0,
    systemHealth: 'good',
    usersByRole: {},
    jobsByStatus: {},
    applicationsByStatus: {},
    monthlyGrowth: {
      users: 0,
      jobs: 0,
      applications: 0
    }
  });

  const [systemAlerts, setSystemAlerts] = useState<AlertType[]>([]);
  const [recentActivity, setRecentActivity] = useState<ActivityType[]>([]);

  // Dialog states
  const [userDialog, setUserDialog] = useState({ open: false, user: null as any, mode: 'view' as 'view' | 'edit' | 'create' });
  const [systemDialog, setSystemDialog] = useState({ open: false, type: 'backup' as 'backup' | 'maintenance' | 'settings' });

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const dashboardStats = await superAdminService.getDashboardStats();
      setStats(dashboardStats);

      const alerts = await superAdminService.getSystemAlerts();
      setSystemAlerts(alerts);

      const activity = await superAdminService.getRecentActivity();
      setRecentActivity(activity);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <LinearProgress />
        <Typography variant="h6" sx={{ mt: 2, textAlign: 'center' }}>
          Loading Super Admin Dashboard...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <DashboardHeader stats={stats} />
      
      <DashboardStats stats={stats} />

      <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 400px', minWidth: '400px' }}>
          <RecentActivity activities={recentActivity} />
        </Box>
        <Box sx={{ flex: '1 1 400px', minWidth: '400px' }}>
          <SystemAlerts alerts={systemAlerts} />
        </Box>
      </Box>

      <UserDialog
        open={userDialog.open}
        onClose={() => setUserDialog({ open: false, user: null, mode: 'view' })}
        mode={userDialog.mode}
        user={userDialog.user}
      />

      <SystemDialog
        open={systemDialog.open}
        onClose={() => setSystemDialog({ open: false, type: 'backup' })}
        type={systemDialog.type}
      />
    </Container>
  );
};

export default SuperAdminDashboard;
